/**
 * src/formValidation.js
 *
 * Details-step validation for the booking wizard. Dependency-free so it runs
 * under vitest's Node environment.
 */

/**
 * Dynamic form validation based on service config.
 * @param {object} f - form values
 * @param {object} service - selected service (Graph response)
 * @param {array}  customQuestions - business-level custom questions
 */
export const validateForm = (f, service, customQuestions, autofilled = {}) => {
  const e = {};
  // Name and email are always required
  if (!f.fullName?.trim()) e.fullName = "Required";
  if (!f.email?.trim()) e.email = "Required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Invalid email";

  if (!f.company?.trim()) e.company = "Required";
  if (f.phone !== undefined) {
    if (!f.phone?.trim()) e.phone = "Required";
    else {
      const digits = f.phone.replace(/\D/g, "");
      if (digits.length < 7) e.phone = "Enter a valid phone number";
    }
  }

  // Validate custom questions marked as required on this service — only those
  // whose definition is loaded, i.e. the ones DetailsStep actually renders.
  // Requiring an unrendered question blocks submit with no visible error.
  // Autofilled questions are answered from a built-in field (validated above).
  const known = new Set((customQuestions || []).map(q => q.id));
  const svcQuestions = service?.customQuestions || [];
  for (const sq of svcQuestions) {
    if (sq.isRequired && known.has(sq.questionId) && !autofilled[sq.questionId]) {
      const key = `cq_${sq.questionId}`;
      if (!f[key]?.trim()) e[key] = "Required";
    }
  }

  return e;
};

// ── Duplicate questions → built-in fields ────────────────────────────────────
// A service's Bookings custom question can ask for something the Details form
// already collects (e.g. "Company Name" vs the built-in Company field). Such a
// question is hidden and answered from the built-in field, so the visitor
// answers once and Bookings still receives the answer.
// settings.customQuestionAutofill = { mode: "auto" | "manual" | "off",
//   map: [{ questionId, formField: "fullName" | "email" | "company" | "phone" | "none" }] }
//   auto   — explicit map entries, plus name detection for the rest (default)
//   manual — explicit map entries only
//   off    — never autofill; every question is shown
//   formField "none" on an entry excludes that question from detection.

export const AUTOFILL_FORM_FIELDS = ["fullName", "email", "company", "phone"];

// Matched against the normalised question name (lower-case, "(…)" and
// punctuation removed). Whole-name matches only, so "Company size" or
// "Company website" are never treated as the company field.
const AUTO_DETECT = {
  company:  /^(company|company name|organi[sz]ation|organi[sz]ation name|business name)$/,
  email:    /^(e-?mail|e-?mail address|your e-?mail)$/,
  phone:    /^(phone|phone number|mobile|mobile number|telephone|telephone number)$/,
  fullName: /^(name|full name|your name)$/,
};

const normaliseName = (s) => String(s || "").toLowerCase()
  .replace(/\(.*?\)/g, " ")
  .replace(/[^a-z\s-]/g, " ")
  .replace(/\s+/g, " ")
  .trim();

/**
 * Which of a service's custom questions are answered from built-in fields.
 * @param {object[]} serviceQuestions - service.customQuestions ({ questionId, isRequired })
 * @param {object[]} definitions      - loaded custom-question definitions ({ id, displayName, answerInputType })
 * @param {object}  [config]          - settings.customQuestionAutofill
 * @returns {Object<string,string>} questionId → built-in form field
 */
export const resolveAutofilledQuestions = (serviceQuestions, definitions, config) => {
  const mode = config?.mode || "auto";
  if (mode === "off") return {};
  const defs = new Map((definitions || []).map(q => [q.id, q]));
  const explicit = new Map((Array.isArray(config?.map) ? config.map : [])
    .filter(m => m && typeof m.questionId === "string")
    .map(m => [m.questionId, m.formField]));
  const out = {};
  for (const sq of serviceQuestions || []) {
    const id = sq?.questionId;
    if (!id || !defs.has(id)) continue; // only questions the form would render
    // Choice questions (radio/dropdown) can't take a free-text answer, so a
    // built-in field's value would be invalid — never autofill them.
    const def = defs.get(id);
    if (def.answerInputType && def.answerInputType !== "text") continue;
    if (explicit.has(id)) {
      const field = explicit.get(id);
      if (AUTOFILL_FORM_FIELDS.includes(field)) out[id] = field;
      continue; // "none" (or unknown) ⇒ show the question
    }
    if (mode !== "auto") continue;
    const name = normaliseName(def.displayName);
    const field = AUTOFILL_FORM_FIELDS.find(f => AUTO_DETECT[f].test(name));
    if (field) out[id] = field;
  }
  return out;
};

/** Merge custom-question definitions by id (Graph ids are GUIDs, unique across businesses). */
export const mergeQuestionsById = (prev, next) => {
  const byId = new Map((prev || []).map(q => [q.id, q]));
  for (const q of next || []) byId.set(q.id, q);
  return [...byId.values()];
};
